import React, { useState, useEffect } from 'react';
import { FileCheck, Calendar, ChevronDown, ChevronUp, Loader2, Settings } from 'lucide-react';
import StatusBadge, { StatusIndicator } from './StatusBadge';
import ReferencesTable from './ReferencesTable';
import SortButton from './SortButton';
import SendEmailButton from './SendEmailButton';
import CopyLinkButton from './CopyLinkButton';
import EvaluateWithAIButton from './EvaluateWithAIButton';
import { sortCandidates, SortField, SortDirection } from '../../utils/sorting';
import { candidatesApi, Candidate } from '../../api/candidates';
import { referencesApi, ReferenceWithAnswers } from '../../api/references';
import { jobsApi } from '../../api/jobs';
import { useLanguage } from '../../contexts/LanguageContext';

interface CandidatesTableProps {
  selectedJobId: string;
  candidatesData?: Candidate[];
  isJobCompleted?: boolean;
}

// Adapter to transform API candidate data to the format expected by the component
const adaptCandidate = (candidate: Candidate) => {
  // Count completed references (those with filled_form_at truthy value)
  const completedRefs = candidate.references ? 
    candidate.references.filter(ref => ref.filled_form_at).length : 0;
  
  // Get total references for this candidate - use actual references provided, not just required
  const totalRefs = candidate.references ? candidate.references.length : 0;
  
  // Debug log for reference counts with VERY CLEAR formatting
  console.log('------------- REFERENCE DEBUG ---------------');
  console.log('Candidate:', candidate.full_name || candidate.email);
  console.log('References array:', candidate.references);
  console.log('References with filled_form_at truthy value:', 
    candidate.references ? 
      candidate.references.filter(ref => ref.filled_form_at) : 
      'No references');
  console.log('References array length:', candidate.references ? candidate.references.length : 0);
  console.log('Completed count:', completedRefs, 'Total count:', totalRefs);
  console.log('Reference count values should be:', completedRefs + '/' + totalRefs);
  console.log('--------------------------------------------');
  
  // Force the references count directly in returned object
  const transformedCandidate = {
    ...candidate,
    id: candidate.id,
    name: candidate.full_name,
    email: candidate.email,
    status: candidate.status,
    jobId: candidate.jobId,
    appliedDate: candidate.created_at,
    formSubmittedDate: candidate.form_filled_at,
    // Count completed references (those with filled_form_at date)
    referencesCompleted: completedRefs,
    // Use actual number of references available for this candidate
    referencesRequired: totalRefs,
    // Force the reference count to be correct
    _referenceCount: `${completedRefs}/${totalRefs}`
  };
  
  console.log('Final transformed candidate references:', 
    transformedCandidate.referencesCompleted + '/' + transformedCandidate.referencesRequired);
  
  return transformedCandidate;
};

const CandidatesTable = ({ selectedJobId, candidatesData, isJobCompleted = false }: CandidatesTableProps) => {
  const { t } = useLanguage();
  const [expandedCandidates, setExpandedCandidates] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection | null>(null);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [references, setReferences] = useState<ReferenceWithAnswers[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [jobsMap, setJobsMap] = useState<Record<string, any>>({});

  // Load all jobs to get reference requirements
  useEffect(() => {
    const loadJobs = async () => {
      try {
        const jobsData = await jobsApi.getAll();
        const jobsMapping = jobsData.reduce((acc, job) => {
          acc[job.id] = job;
          return acc;
        }, {} as Record<string, any>);
        setJobsMap(jobsMapping);
      } catch (err) {
        console.error('Failed to load jobs:', err);
      }
    };
    
    loadJobs();
  }, []);

  // Load candidates when selected job changes or when candidatesData prop changes
  useEffect(() => {
    const loadCandidates = async () => {
      setLoading(true);
      setError(null);
      
      try {
        let candidatesFromSource;
        
        // Use provided candidatesData if available, otherwise fetch from API
        if (candidatesData) {
          candidatesFromSource = candidatesData;
        } else {
          candidatesFromSource = await candidatesApi.getAll();
        }
        
        // Apply job filter if needed
        if (selectedJobId) {
          candidatesFromSource = candidatesFromSource.filter(c => c.jobId === selectedJobId);
        }
        
        // Transform candidates to the format expected by the component
        const adaptedCandidates = candidatesFromSource.map(candidate => {
          // Use the adaptCandidate function without overriding referencesRequired
          return adaptCandidate(candidate);
        });
        
        setCandidates(adaptedCandidates);
      } catch (err) {
        console.error('Failed to load candidates:', err);
        setError(t('candidates.table.error'));
      } finally {
        setLoading(false);
      }
    };
    
    if (Object.keys(jobsMap).length > 0) {
      loadCandidates();
    }
  }, [selectedJobId, jobsMap, candidatesData]);

  // Load references for all candidates
  useEffect(() => {
    const loadReferences = async () => {
      try {
        const referencesData = await referencesApi.getAll();
        setReferences(referencesData);
      } catch (err) {
        console.error('Failed to load references:', err);
      }
    };
    
    loadReferences();
  }, []);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  let filteredCandidates = [...candidates];

  if (sortField && sortDirection) {
    filteredCandidates = sortCandidates(filteredCandidates, sortField, sortDirection);
  }

  const toggleCandidate = (candidateId: string) => {
    const newExpanded = new Set(expandedCandidates);
    if (newExpanded.has(candidateId)) {
      newExpanded.delete(candidateId);
    } else {
      newExpanded.add(candidateId);
    }
    setExpandedCandidates(newExpanded);
  };

  const getCandidateReferences = (candidateId: string) => {
    // Ensure we handle undefined references
    if (!references || !Array.isArray(references)) {
      return [];
    }
    return references.filter(ref => ref.candidate_id === candidateId);
  };

  // Mobile card view for each candidate
  const renderMobileCard = (candidate: any) => (
    <div key={candidate.id} className="bg-white p-4 rounded-lg border mb-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-medium text-gray-900">{candidate.name}</h3>
          <p className="text-sm text-gray-500">{candidate.email}</p>
        </div>
        <button
          onClick={() => toggleCandidate(candidate.id)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          {expandedCandidates.has(candidate.id) ? (
            <ChevronUp className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          )}
        </button>
      </div>

      <div className="mb-4">
        <div className="text-sm text-gray-500 mb-1">References</div>
        <div className="flex items-center gap-2">
          <FileCheck className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium">
            {candidate._referenceCount || `${candidate.referencesCompleted} / ${candidate.referencesRequired}`}
          </span>
        </div>
        <div className="w-full h-1.5 bg-gray-100 rounded-full mt-1 overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-300"
            style={{
              width: `${!candidate.formSubmittedDate || !candidate.referencesRequired 
                ? 0 
                : Math.min(100, (candidate.referencesCompleted / (candidate.referencesRequired || 1)) * 100)}%`
            }}
          />
        </div>
      </div>

      <div className="flex items-center justify-between mb-2">
        <StatusBadge 
          formFilled={!!candidate.formSubmittedDate}
          referencesCompleted={candidate.referencesCompleted}
          referencesTotal={candidate.referencesRequired}
        />
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Calendar className="w-4 h-4" />
          {candidate.formSubmittedDate ? new Date(candidate.formSubmittedDate).toLocaleDateString() : t('common.notSubmitted')}
        </div>
      </div>
      
      <div className="flex flex-col gap-2 mt-3">
        <div className="text-xs font-medium text-gray-500">{t('candidates.table.columns.actions')}</div>
        <div className="flex gap-2 flex-wrap">
          {!isJobCompleted && (
            <SendEmailButton candidateId={candidate.id} email={candidate.email} disabled={isJobCompleted} />
          )}
          <EvaluateWithAIButton candidateId={candidate.id} candidateName={candidate.name} />
        </div>
      </div>

      {expandedCandidates.has(candidate.id) && (
        <div className="mt-4 mb-2 rounded-lg border border-blue-100 overflow-hidden shadow-sm">
          <div className="bg-blue-50/40 py-2 px-4 border-b border-blue-100">
            <h4 className="text-sm font-semibold text-blue-700">{t('candidates.table.columns.referencesFor', { name: candidate.name })}</h4>
          </div>
          <div className="p-2">
            <ReferencesTable 
              references={getCandidateReferences(candidate.id)}
              candidateId={candidate.id} 
              jobId={candidate.jobId}
              isJobCompleted={isJobCompleted}
              onReferenceAdded={() => window.location.reload()}
            />
          </div>
        </div>
      )}
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-xl border shadow-sm p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500 border-r-2 mx-auto mb-4"></div>
        <p className="text-gray-500">{t('common.loadingCandidates')}</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-xl border shadow-sm p-8 text-center">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-4">
          <p>{error}</p>
        </div>
        <button 
          className="text-blue-500 hover:text-blue-700"
          onClick={() => window.location.reload()}
        >
          {t('common.tryAgain')}
        </button>
      </div>
    );
  }

  // Empty state
  if (filteredCandidates.length === 0) {
    return (
      <div className="bg-white rounded-xl border shadow-sm p-8 text-center">
        <p className="text-gray-500 mb-4">{t('common.noCandidatesFound')}</p>
        {selectedJobId && (
          <p className="text-sm text-gray-400">
            {t('common.selectDifferentJob')}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50/80 border-b">
              <th className="w-10 px-4 py-4"></th>
              <th className="px-6 py-4 text-left">
                <SortButton
                  icon={FileCheck}
                  label={t('candidates.table.columns.candidate')}
                  active={sortField === 'name'}
                  direction={sortField === 'name' ? sortDirection : null}
                  onClick={() => handleSort('name')}
                />
              </th>
              <th className="px-6 py-4 text-left">
                <SortButton
                  icon={FileCheck}
                  label={t('candidates.table.columns.references')}
                  active={sortField === 'referencesCompleted'}
                  direction={sortField === 'referencesCompleted' ? sortDirection : null}
                  onClick={() => handleSort('referencesCompleted')}
                />
              </th>
              <th className="px-6 py-4 text-left">
                <SortButton
                  icon={FileCheck}
                  label={t('candidates.table.columns.status')}
                  active={sortField === 'status'}
                  direction={sortField === 'status' ? sortDirection : null}
                  onClick={() => handleSort('status')}
                />
              </th>
              <th className="px-6 py-4 text-left">
                <SortButton
                  icon={Calendar}
                  label={t('candidates.table.columns.formSubmitted')}
                  active={sortField === 'formSubmittedDate'}
                  direction={sortField === 'formSubmittedDate' ? sortDirection : null}
                  onClick={() => handleSort('formSubmittedDate')}
                />
              </th>
              <th className="px-6 py-4 text-left">
                <div className="flex items-center gap-2 text-gray-500">
                  <Settings className="w-4 h-4" />
                  <span className="text-xs font-medium uppercase">{t('common.actions')}</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredCandidates.map((candidate) => (
              <React.Fragment key={candidate.id}>
                <tr className={`group hover:bg-gray-50/50 transition-colors ${
                  expandedCandidates.has(candidate.id) ? 'bg-blue-50/20' : ''
                }`}>
                  <td className="px-4 py-4">
                    <button
                      onClick={() => toggleCandidate(candidate.id)}
                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      {expandedCandidates.has(candidate.id) ? (
                        <ChevronUp className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <div className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                        {candidate.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {candidate.email}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <FileCheck className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="text-sm text-gray-900">
                          {candidate._referenceCount || `${candidate.referencesCompleted} / ${candidate.referencesRequired}`}
                        </div>
                        <div className="w-24 h-1.5 bg-gray-100 rounded-full mt-1 overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full transition-all duration-300"
                            style={{
                              width: `${!candidate.formSubmittedDate || !candidate.referencesRequired 
                                ? 0 
                                : Math.min(100, (candidate.referencesCompleted / (candidate.referencesRequired || 1)) * 100)}%`
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <StatusIndicator 
                        formFilled={!!candidate.formSubmittedDate}
                        referencesCompleted={candidate.referencesCompleted}
                        referencesTotal={candidate.referencesRequired}
                      />
                      <span className="inline-block ml-1.5">
                        <StatusBadge 
                          formFilled={!!candidate.formSubmittedDate}
                          referencesCompleted={candidate.referencesCompleted}
                          referencesTotal={candidate.referencesRequired}
                        />
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar className="w-4 h-4" />
                      {candidate.formSubmittedDate ? new Date(candidate.formSubmittedDate).toLocaleDateString() : t('common.notSubmitted')}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      {!isJobCompleted && (
                        <SendEmailButton candidateId={candidate.id} email={candidate.email} disabled={isJobCompleted} />
                      )}
                      <EvaluateWithAIButton candidateId={candidate.id} candidateName={candidate.name} />
                    </div>
                  </td>
                </tr>
                {expandedCandidates.has(candidate.id) && (
                  <tr>
                    <td colSpan={7} className="px-0 py-0 border-b-4 border-gray-100">
                      <div className="mx-4 my-4 rounded-lg border border-blue-100 overflow-hidden shadow-sm">
                        <div className="bg-blue-50/40 py-2 px-6 border-b border-blue-100">
                          <h4 className="text-sm font-semibold text-blue-700">{t('candidates.table.columns.referencesFor', { name: candidate.name })}</h4>
                        </div>
                        <div className="px-2 py-2 bg-white">
                          <ReferencesTable 
                            references={getCandidateReferences(candidate.id)}
                            candidateId={candidate.id} 
                            jobId={candidate.jobId}
                            isJobCompleted={isJobCompleted}
                            onReferenceAdded={() => window.location.reload()}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lg:hidden p-4">
        {filteredCandidates.map(renderMobileCard)}
      </div>
    </div>
  );
};

export default CandidatesTable;